/**
 * What the semantic linters can know about a node's parent type and visibility. Both
 * meet the same limit: a node instanced, or overridden inside an instance, takes its
 * type and properties from a scene this linter never opens.
 */
import { isTypeUnknowable } from '../parser/typeUnknowable.js';
import { findParentNode } from './linterUtils.js';
import { isCatalogedType } from '../godot/classdb/nodeBaseTypes.js';
import { descendsFromClass } from '../godot/classdb/classBaseTypes.js';
import { boolSlotValue } from '../godot/variant/variantBool.js';
// Re-exported, not re-spelled: `seatScene` asks the same question at
// every descent of a `parent=` path, and the parser cannot import the linter.
export { isTypeUnknowable };
/**
 * Resolve `node`'s parent against `wantedType`, subclasses included, with the unknowable
 * exemption in one place, since a hand-written copy at a call site can drop it.
 */
export function parentTypeVerdict(scene, node, wantedType) {
    const step = knownParent(scene, node);
    if (step.kind !== 'known')
        return step;
    const { parent } = step;
    if (descendsFromClass(parent.type, wantedType))
        return { kind: 'satisfied', parent };
    return { kind: 'mismatch', parent };
}
/**
 * `node`'s parent, or the reason no rule may reason about it. With {@link parentLookup}, it
 * is the only way out of this module to a parent whose type may be read. `findParentNode` hands back the raw heading,
 * where a forgotten check is invisible. {@link parentIdentity} is the second door, for a
 * caller that reads no type.
 */
export function knownParent(scene, node) {
    return parentLookup(findParentNode(scene.nodes, node));
}
/**
 * The {@link ParentLookup} for a parent some tree already found, or for none at the root.
 * A tree other than this file's, such as the previewer's live tree, finds its own parent
 * and asks the same knowability question here.
 */
export function parentLookup(parent) {
    if (!parent)
        return { kind: 'root' };
    // A type goes unread two ways: declared in another scene, or outside the catalog. Godot's
    // check is a runtime `cast_to` against a ClassDB with every extension registered
    // (collision_shape_3d.cpp:125-128), and `descendsFromClass` says false for "not a subclass"
    // and "never heard of it" alike.
    if (isTypeOpaque(parent))
        return { kind: 'unknowable' };
    return { kind: 'known', parent };
}
/**
 * Whether this node's class is one no verdict may be drawn from, the two ways
 * {@link knownParent} declines, asked of any node. The child-side check,
 * `hasChildOfType` (`childType.ts`), needs it for a GDExtension shape provider.
 */
export function isTypeOpaque(node) {
    return isTypeUnknowable(node) || !isCatalogedType(node.type);
}
/**
 * `node`'s parent whatever its class, or null at the root. A NodePath `..` is `get_parent()`,
 * which needs identity, never type, so a {@link knownParent} decline would stop the walk.
 * Safe only because the caller reads no `.type` off it: `resolveNodePath` gates its result
 * through {@link isTypeUnknowable}.
 */
export function parentIdentity(scene, node) {
    return findParentNode(scene.nodes, node);
}
/**
 * Climb `node`'s ancestors, nearest first, until `visit` returns a value. `visit` sees only
 * an ancestor whose type this file states and the catalog knows: each step goes through
 * `knownParent`, so a bare `ancestor.type` or `descendsFromClass` read inside `visit` is correct.
 * An instanced, override, typeless or GDExtension ancestor ends the walk at `unknowable`.
 */
export function searchAncestors(scene, node, visit) {
    return climbAncestors(node, (child) => knownParent(scene, child), visit);
}
/**
 * {@link searchAncestors} over any tree: `parentOf` takes each step, so a walk shared with
 * the previewer climbs the live tree with the same rules.
 */
export function climbAncestors(node, parentOf, visit) {
    // `undefined` climbs on, anything else stops, and a caller that declines returns its
    // own sentinel. Godot's walks differ too much to fold in (`Bone2D` stops at the first
    // non-Bone2D, the `clip_children` checks read to the root), so that varies in `visit`.
    let current = node;
    for (;;) {
        const step = parentOf(current);
        if (step.kind === 'root')
            return { kind: 'exhausted' };
        if (step.kind === 'unknowable')
            return { kind: 'unknowable' };
        const value = visit(step.parent);
        if (value !== undefined)
            return { kind: 'found', value };
        current = step.parent;
    }
}
/**
 * Visit every ancestor whose type this file states, nearest first, to the root.
 * `CanvasItem::get_configuration_warnings()` (`canvas_item.cpp:1302-1320`) walks
 * `n = n->get_parent()` to the top with no type test, so an unclassifiable ancestor is
 * skipped. A chain-terminating walk declines instead, since the skipped one might end it.
 */
export function sweepAncestors(scene, node, visit) {
    // A skipped ancestor can only add a hit, never withdraw one, so nothing is reported
    // about it. The engine's early exit once every warning fired (`canvas_item.cpp:1321-1325`)
    // is an optimisation with nothing observable riding on it.
    let current = findParentNode(scene.nodes, node);
    while (current) {
        if (!isTypeUnknowable(current))
            visit(current);
        current = findParentNode(scene.nodes, current);
    }
}
/**
 * The parent a `satisfied` or `mismatch` verdict carries, or null. Rules ask it a second
 * question, as `CollisionPolygon2D` tests its parent for `CollisionObject2D`, then `Area2D`
 * (`collision_polygon_2d.cpp:235-254`). Safe only here, past `isTypeUnknowable`.
 */
export function verdictParent(verdict) {
    return verdict.kind === 'satisfied' || verdict.kind === 'mismatch' ? verdict.parent : null;
}
/**
 * How a diagnostic names where the node sits, for a `mismatch` or `root`: Godot phrases
 * these warnings around placement, so every one needs the same clause.
 */
export function placementPhrase(verdict) {
    return verdict.kind === 'mismatch' ? `a child of a ${verdict.parent.type} node` : 'the scene root';
}
/**
 * True when the node sets `visible = false` in this file. Godot gates several warnings on
 * `is_visible()` (`node_3d.cpp:1127-1130`), the local flag, not `is_visible_in_tree()`.
 * The cascade reads each ancestor with it too: CanvasItem (`canvas_item.cpp:1471`), Node3D
 * (`node_3d.cpp:1541`), CanvasLayer (`canvas_layer.cpp:341`) and Window (`window.cpp:3436`) spell it `visible`.
 */
export function isExplicitlyHidden(node) {
    return boolSlotValue(node.rawProperties.visible) === false;
}
/**
 * `Node3D::is_visible_in_tree()` (`node_3d.cpp:1131-1143`) walks `s = s->data.parent`, and
 * `data.parent` is `cast_to<Node3D>(get_parent())` (`node_3d.cpp:150`), so the chain is the
 * contiguous Node3D run, and the first non-Node3D ancestor ends it.
 */
function node3DCascade(scene, node) {
    let current = findParentNode(scene.nodes, node);
    while (current) {
        if (isTypeUnknowable(current))
            return 'unknowable';
        // `descendsFromClass` is false for two unrelated reasons, and only one of them
        // ends the chain: a GDExtension class this build has never heard of may
        // well be a Node3D. `parentTypeVerdict` guards the same case.
        if (!isCatalogedType(current.type))
            return 'unknowable';
        if (!descendsFromClass(current.type, 'Node3D'))
            return 'visible';
        if (isExplicitlyHidden(current))
            return 'hidden';
        current = findParentNode(scene.nodes, current);
    }
    return 'visible';
}
/**
 * `CanvasItem::is_visible_in_tree()` (`canvas_item.cpp:62-64`: `visible && parent_visible_in_tree`).
 * `NOTIFICATION_ENTER_TREE` (`canvas_item.cpp:306-352`) sets the parent half from the immediate
 * parent: a CanvasItem's own `is_visible_in_tree()`, so the run cascades, else a CanvasLayer's
 * `cl->is_visible()` (`canvas_layer.cpp:75`), else the first Viewport ancestor.
 */
function canvasItemCascade(scene, node) {
    let current = findParentNode(scene.nodes, node);
    while (current) {
        if (isTypeUnknowable(current))
            return 'unknowable';
        // See `node3DCascade`: an uncataloged ancestor may be a CanvasItem.
        if (!isCatalogedType(current.type))
            return 'unknowable';
        if (!descendsFromClass(current.type, 'CanvasItem'))
            break;
        if (isExplicitlyHidden(current))
            return 'hidden';
        current = findParentNode(scene.nodes, current);
    }
    if (!current)
        return 'visible';
    // The layer's own key only: the chain ends there.
    if (descendsFromClass(current.type, 'CanvasLayer')) {
        return isExplicitlyHidden(current) ? 'hidden' : 'visible';
    }
    // The Viewport search starts at that same parent and skips whatever is not a Viewport,
    // so an unknowable ancestor still matters. A Window gives `window->is_visible()`
    // (`window.cpp:1166-1169`), any other Viewport `true`, and a saved scene's root has none.
    while (current) {
        if (isTypeUnknowable(current))
            return 'unknowable';
        // Same reason the CanvasItem run declines on one: `descendsFromClass` is false
        // for "not a Window" and for "never heard of it" alike, and a GDExtension
        // class in this position may well be a Window whose own `visible` decides.
        if (!isCatalogedType(current.type))
            return 'unknowable';
        if (descendsFromClass(current.type, 'Window')) {
            return isExplicitlyHidden(current) ? 'hidden' : 'visible';
        }
        if (descendsFromClass(current.type, 'Viewport'))
            return 'visible';
        current = findParentNode(scene.nodes, current);
    }
    return 'visible';
}
/**
 * Godot's `is_visible_in_tree()` for `node`, from this file alone. Only an explicit
 * `visible = false` hides, never a class default: `Popup` (`popup.cpp:218`) and
 * `AcceptDialog` (`dialogs.cpp:466`) `set_visible(false)` in their constructors, and
 * resolving absence would silence every warning under one.
 */
function visibleInTreeVerdict(scene, node) {
    if (isExplicitlyHidden(node))
        return 'hidden';
    if (descendsFromClass(node.type, 'CanvasItem'))
        return canvasItemCascade(scene, node);
    if (descendsFromClass(node.type, 'Node3D'))
        return node3DCascade(scene, node);
    // Neither family: no `is_visible_in_tree()` and no gated warning to suppress.
    return 'visible';
}
/**
 * True when a warning Godot gates on `is_visible_in_tree()` must stay silent, on
 * `unknowable` too: a guess would report a misconfiguration the author cannot see here.
 */
export function hiddenOrUnknowableInTree(scene, node) {
    return visibleInTreeVerdict(scene, node) !== 'visible';
}
//# sourceMappingURL=parentType.js.map